function _inheritsLoose(subClass, superClass) { subClass.prototype = Object.create(superClass.prototype); subClass.prototype.constructor = subClass; subClass.__proto__ = superClass; }

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { LiveProvider, LivePreview } from 'react-live';
import debounce from 'lodash.debounce';
import Tooltip from '@homeaway/react-tooltip';
import { SVGDefinitions, SVGIcon } from '@homeaway/react-svg';
import LiveDemoError from '../LiveDemoError/LiveDemoError';
import LiveEditorLineNumbers from '../LiveEditorLineNumbers/LiveEditorLineNumbers';
import classnames from 'classnames';
import PrismTheme from './LiveDemoTheme';
import "./LiveDemo.css";

var LiveDemo = /*#__PURE__*/function (_Component) {
  _inheritsLoose(LiveDemo, _Component);

  function LiveDemo(props) {
    var _this;

    _this = _Component.call(this, props) || this; // Setting the debounce time to 300ms as it looks like the max debounce
    // for components is 250ms. This will ensure the height of the demo
    // preview is calculated after the component in the demo updates.

    _this.checkHeight = function () {
      var newHeight = _this.demo ? _this.demo.scrollHeight : 0;

      if (_this.heightChanged || _this.contentHeight !== newHeight && _this.liveDemoEditor && _this.liveDemoError && _this.liveDemoPreview && _this.liveDemoFooter) {
        _this.contentHeight = newHeight;
        _this.heightChanged = false;
        window.setTimeout(function () {
          _this.props.onSizeChange({
            editorHeight: _this.props.hideChrome ? 0 : _this.liveDemoEditor.clientHeight,
            errorHeight: _this.liveDemoError.clientHeight ? _this.liveDemoError.clientHeight + 32 : 0,
            previewHeight: _this.liveDemoPreview.offsetHeight,
            footerHeight: _this.liveDemoFooter.clientHeight
          });
        }, 0);
      }
    };

    _this.handleShowCodeClick = function () {
      _this.props.onShowCodeClicked();
    };

    _this.handleEditorChange = function (code) {
      _this.checkHeight();

      _this.props.onChange(code);

      _this.setState({
        code: code
      });
    };

    _this.handleErrorChange = function (error) {
      _this.props.onErrorChange(error);

      _this.checkHeight();
    };

    _this.debouncedCheckHeight = debounce(_this.checkHeight, 300);
    _this.state = {
      code: props.content,
      lineNumberHeight: 0,
      parentNode: null
    };
    return _this;
  }

  var _proto = LiveDemo.prototype;

  _proto.componentDidMount = function componentDidMount() {
    this.checkHeight();

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.debouncedCheckHeight, false);
    }

    this.setState({
      parentNode: this.parentNode
    });
  };

  _proto.componentDidUpdate = function componentDidUpdate(prevProps) {
    if (prevProps === void 0) {
      prevProps = {};
    }

    var content = this.props.content;

    if (prevProps.content !== content) {
      this.setState({
        code: content
      });
    }

    this.checkHeight();
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    // Remove the window resizing event listener.
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.debouncedCheckHeight, false);
    }
  };

  _proto.render = function render() {
    var _this2 = this;

    var _this$props = this.props,
        clearStyle = _this$props.clearStyle,
        hideChrome = _this$props.hideChrome,
        demoSize = _this$props.demoSize,
        editorOpen = _this$props.editorOpen,
        minHeight = _this$props.minHeight,
        noInline = _this$props.noInline,
        scope = _this$props.scope,
        hideCodeButton = _this$props.hideCodeButton;
    var editorClasses = classnames('LiveDemo__editor', {
      'LiveDemo__editor--hidden': demoSize === 'xs' || hideChrome
    });
    var codeToggleClass = classnames({
      'text-link': editorOpen
    });
    var previewStyle = demoSize === 'xs' ? null : {
      minHeight: minHeight,
      // Adding a minimal padding to the LivePreview in order to get around
      // margin collapsing that was preventing the LivePreview height from
      // accounting for margin in the child elements:
      // https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Box_Model/Mastering_margin_collapsing
      paddingBottom: '1px'
    };
    var footerStyle = classnames('LiveDemo__footer', {
      'LiveDemo__footer--hidden': hideCodeButton || demoSize === 'xs' || hideChrome
    });
    var previewClasses = classnames('LiveDemo__LivePreview', {
      'bg--color-light': !hideChrome,
      'LiveDemo__LivePreview--xs': demoSize === 'xs',
      'LiveDemo__LivePreview--default': !clearStyle,
      'LiveDemo__LivePreview--chromeless': hideChrome
    });
    return /*#__PURE__*/React.createElement("div", {
      className: "LiveDemo",
      ref:
      /* istanbul ignore next */
      function ref(p) {
        return _this2.parentNode = p;
      }
    }, /*#__PURE__*/React.createElement(LiveProvider, {
      noInline: noInline,
      scope: scope,
      code: this.state.code,
      mountStylesheet: false,
      theme: PrismTheme
    }, /*#__PURE__*/React.createElement("div", {
      ref:
      /* istanbul ignore next */
      function ref(demo) {
        return _this2.demo = demo;
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: previewClasses,
      ref:
      /* istanbul ignore next */
      function ref(c) {
        return _this2.liveDemoPreview = c;
      }
    }, /*#__PURE__*/React.createElement(LivePreview, {
      style: previewStyle
    })), /*#__PURE__*/React.createElement("div", {
      className: footerStyle,
      ref:
      /* istanbul ignore next */
      function ref(c) {
        return _this2.liveDemoFooter = c;
      }
    }, /*#__PURE__*/React.createElement(SVGDefinitions, null, /*#__PURE__*/React.createElement("svg", {
      id: "SVG_VIEW_CODE__24",
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 24 24"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M6 8l-4 4 4 4m12-8l4 4-4 4m-8 4l4-16",
      fill: "none",
      strokeLinecap: "square"
    }))), /*#__PURE__*/React.createElement(Tooltip, {
      wrapperClassName: "LiveDemo__toggle",
      centered: true,
      content: editorOpen ? 'Hide Live Code' : 'View Live Code',
      parentNode: this.state.parentNode
    }, /*#__PURE__*/React.createElement("button", {
      className: "LiveDemo__toggle-button",
      onClick: this.handleShowCodeClick
    }, /*#__PURE__*/React.createElement(SVGIcon, {
      className: codeToggleClass,
      id: "VIEW_CODE__24"
    })))), /*#__PURE__*/React.createElement("div", {
      ref:
      /* istanbul ignore next */
      function ref(c) {
        return _this2.liveDemoError = c;
      }
    }, /*#__PURE__*/React.createElement(LiveDemoError, {
      onErrorChange: this.handleErrorChange
    })), /*#__PURE__*/React.createElement("div", {
      className: editorClasses,
      ref:
      /* istanbul ignore next */
      function ref(c) {
        return _this2.liveDemoEditor = c;
      }
    }, /*#__PURE__*/React.createElement(LiveEditorLineNumbers, {
      onChange: this.handleEditorChange
    })))));
  };

  return LiveDemo;
}(Component);

LiveDemo.propTypes = {
  clearStyle: PropTypes.bool,
  content: PropTypes.string,
  demoSize: PropTypes.oneOf(['lg', 'xs']),
  editorOpen: PropTypes.bool,
  hideChrome: PropTypes.bool,
  minHeight: PropTypes.string,
  noInline: PropTypes.bool,
  hideCodeButton: PropTypes.bool,
  onChange: PropTypes.func,
  onShowCodeClicked: PropTypes.func,
  onErrorChange: PropTypes.func,
  onSizeChange: PropTypes.func,
  scope: PropTypes.object // eslint-disable-line react/forbid-prop-types

};
LiveDemo.defaultProps = {
  clearStyle: false,
  content: '',
  demoSize: 'lg',
  editorOpen: false,
  hideChrome: false,
  noInline: false,
  hideCodeButton: false,
  onChange: function onChange() {},
  onErrorChange: function onErrorChange() {},
  onSizeChange: function onSizeChange() {},
  onShowCodeClicked: function onShowCodeClicked() {},
  scope: {}
};
export default LiveDemo;
//# sourceMappingURL=LiveDemo.js.map